<template>
  <div class="my-people-page">
    <router-link to="/profile" class="back-link">&larr; My Profile</router-link>
    <h1>My People</h1>

    <Spinner :show="loading" message="Loading your people..." />

    <!-- Empty state -->
    <div v-if="!loading && relationships.length === 0" class="empty-state">
      <h2>No one here yet</h2>
      <p>Add someone you care about and we'll help you stay in touch with personalized postcards.</p>
      <button class="button primary" @click="showQuiz = true">Add Your First Person</button>
    </div>

    <!-- People grid -->
    <div v-if="!loading && relationships.length > 0" class="people-grid">
      <PersonCard
        v-for="person in relationships"
        :key="person.id"
        :person="person"
        :pending-proposals="pendingByRelationship[person.id] || []"
        @send="openSendFlow"
        @edit="openEditQuiz"
        @updated="fetchRelationships"
      />
    </div>

    <!-- Add button -->
    <button
      v-if="!loading && relationships.length > 0"
      class="add-person-button button primary"
      @click="showQuiz = true"
    >
      + Add Person
    </button>

    <!-- Add/Edit Quiz Modal -->
    <modal v-if="showQuiz" @close="closeQuiz">
      <template v-slot:header>
        <h2>{{ editTarget ? 'Edit Person' : 'Add a Person' }}</h2>
      </template>
      <template v-slot:body>
        <AddPersonQuiz
          :editPerson="editTarget"
          @complete="onQuizComplete"
          @cancel="closeQuiz"
        />
      </template>
    </modal>

    <!-- Send Flow Modal -->
    <modal v-if="showSendFlow && sendTarget" @close="closeSendFlow">
      <template v-slot:header>
        <h2>Send to {{ sendTarget.name }}</h2>
      </template>
      <template v-slot:body>
        <RelationshipPostcardFlow
          :relationship="sendTarget"
          @close="closeSendFlow"
          @added-to-cart="onAddedToCart"
        />
      </template>
    </modal>
  </div>
</template>

<script>
import Spinner from "@/components/SpinnerComp.vue";
import PersonCard from "@/components/PersonCard.vue";
import AddPersonQuiz from "@/components/AddPersonQuiz.vue";
import RelationshipPostcardFlow from "@/components/RelationshipPostcardFlow.vue";
import Modal from "@/components/ModalView.vue";
import { db, auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs, onSnapshot } from "firebase/firestore";

export default {
  name: "MyPeoplePage",
  components: { Spinner, PersonCard, AddPersonQuiz, RelationshipPostcardFlow, Modal },
  data() {
    return {
      user: null,
      loading: false,
      relationships: [],
      pendingByRelationship: {},
      showQuiz: false,
      editTarget: null,
      showSendFlow: false,
      sendTarget: null,
      authUnsubscribe: null,
      proposalsUnsubscribe: null,
    };
  },
  created() {
    // Store the unsubscribe so the auth listener is torn down on unmount —
    // without it, navigating in/out of this page accumulates listeners.
    this.authUnsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        this.user = user;
        this.fetchRelationships();
        this.subscribeToPendingProposals();
      } else {
        this.user = null;
        this.relationships = [];
        this.pendingByRelationship = {};
        this.teardownProposals();
      }
    });
  },
  beforeUnmount() {
    if (this.authUnsubscribe) this.authUnsubscribe();
    this.teardownProposals();
  },
  methods: {
    async fetchRelationships() {
      if (!this.user) return;
      this.loading = true;
      try {
        const ref = collection(db, "relationships");
        const q = query(
          ref,
          where("userId", "==", this.user.uid),
          where("status", "==", "active")
        );
        const snapshot = await getDocs(q);
        this.relationships = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        this.$store.dispatch("setRelationships", this.relationships);
      } catch (error) {
        this.$toast.error("Error loading your people. Please try again.");
      } finally {
        this.loading = false;
      }
    },
    subscribeToPendingProposals() {
      if (!this.user) return;
      this.teardownProposals();
      // Live subscription so a badge appears the moment the agent proposes a
      // gift and clears as soon as it's decided — where(userId ==) only, so no
      // composite index is needed; we filter to pending + group by person here.
      const q = query(
        collection(db, "giftProposals"),
        where("userId", "==", this.user.uid)
      );
      this.proposalsUnsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const byRel = {};
          snapshot.docs.forEach((d) => {
            const data = d.data();
            if (data.status !== "pending_approval" || !data.relationshipId) return;
            (byRel[data.relationshipId] || (byRel[data.relationshipId] = [])).push({ id: d.id });
          });
          this.pendingByRelationship = byRel;
        },
        () => {
          // A failed proposals read shouldn't break the people grid; badges
          // just won't show.
        },
      );
    },
    teardownProposals() {
      if (this.proposalsUnsubscribe) {
        this.proposalsUnsubscribe();
        this.proposalsUnsubscribe = null;
      }
    },
    openEditQuiz(person) {
      this.editTarget = person;
      this.showQuiz = true;
    },
    closeQuiz() {
      this.showQuiz = false;
      this.editTarget = null;
    },
    onQuizComplete() {
      this.closeQuiz();
      this.fetchRelationships();
    },
    openSendFlow(person) {
      this.sendTarget = person;
      this.showSendFlow = true;
      this.$store.dispatch("setActiveRelationship", person);
    },
    closeSendFlow() {
      this.showSendFlow = false;
      this.sendTarget = null;
    },
    onAddedToCart() {
      this.closeSendFlow();
      this.$toast.success("Postcard added to cart!");
      this.fetchRelationships();
    },
  },
};
</script>

<style scoped>
.my-people-page {
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.back-link {
  display: inline-block;
  color: var(--hollywood-cerise);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 8px;
  transition: color 0.2s;
}

.back-link:hover {
  color: var(--violet-lush);
}

.my-people-page h1 {
  margin-bottom: 24px;
  color: var(--rich-black);
}

.my-people-page p {
  color: var(--violet-lush);
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state h2 {
  color: var(--rich-black);
  margin-bottom: 12px;
}

.empty-state p {
  max-width: 400px;
  margin: 0 auto 24px;
  line-height: 1.5;
}

.people-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.people-grid > * {
  flex: 1 1 calc(33.333% - 20px);
  min-width: 260px;
}

.add-person-button {
  margin-top: 24px;
}

.button {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.button.primary {
  background-color: var(--accent2-color);
  color: white;
}

.button.primary:hover {
  background-color: var(--accent-color);
}

@media (max-width: 768px) {
  .my-people-page {
    padding: 20px;
  }

  .people-grid > * {
    flex: 1 1 100%;
  }
}
</style>
